/*----------------------------------------------------------------------------
    RexInput

    A generic input control for our forms. This is only a text field (no
    checkboxes, etc)

    this will take care of different types of input content (dates, text, etc)

    this will handle not committing the values until the control is blurred
    (or when the form is committed as well)

    NOTE: the controls provided are only used to get consistent name and id
    values in order to associate this control with the hosting surfaces.
    (the host will likely want to associate field names, etc., when change
    events happen, and this allows us to make sure we are consistent)
----------------------------------------------------------------------------*/
import React from "react";
import { Field, Input } from '@fluentui/react-components'
import { FormControls } from "../model/FormControls";
import { HandleInputChangeDelegate } from "../model/HandleInputChangeDelgate";
import { RexDateNoTime } from "../Dates/RexDateNoTime";
import { RexDateTime } from "../Dates/RexDateTime";

export declare type InputType = 'standard' | 'date' | 'integer' | 'date-time';

export class InputTypes
{
    public static Standard: InputType = "standard";
    public static Date: InputType = "date";
    public static DateTime: InputType = "date-time";
    public static Integer: InputType = "integer";
}

/*----------------------------------------------------------------------------
    %%Interface: RexInputProps
----------------------------------------------------------------------------*/
export interface RexInputProps<T>
{
    label: string,
    readonly?: boolean,
    hint?: string,
    required?: boolean;
    controls: FormControls<T>,
    controlName: string,
    style?: any,
    onChange: HandleInputChangeDelegate<string>,
    value?: string | Date | number | RexDateTime,
    inputType?: InputType;
}

/*----------------------------------------------------------------------------
    %%Interface: RexInputState
----------------------------------------------------------------------------*/
export interface RexInputState
{
    unblurredValue?: string
}

export class RexInput<T> extends React.Component<RexInputProps<T>, RexInputState>
{
    constructor(props: RexInputProps<T>)
    {
        super(props);

        this.state =
        {
            unblurredValue: undefined
        }
    }

    /*----------------------------------------------------------------------------
        %%Function: getFluentInputType
        %%Qualified: RexInput.getFluentInputType
    ----------------------------------------------------------------------------*/
    getFluentInputType(): 'text' | 'date'
    {
        switch (this.props.inputType)
        {
        case InputTypes.Date:
            return 'date';
        default:
            return 'text';
        }
    }

    /*----------------------------------------------------------------------------
        %%Function: getValueForField
        %%Qualified: RexInput.getValueForField
    ----------------------------------------------------------------------------*/
    getValueForField(): string
    {
        switch (this.props.inputType)
        {
        case InputTypes.DateTime:
            switch (typeof this.props.value)
            {
            case "string":
                return this.props.value as string;
            case "object":
                if (this.props.value instanceof Date)
                    return RexDateTime.createFromString((this.props.value as Date).toString()).isoString;
                else
                    return (this.props.value as RexDateTime).isoString;
            default:
                throw new Error("Invalid value type for date input type");
            }

        case InputTypes.Date:
            switch (typeof this.props.value)
            {
            case "string":
                return this.props.value as string;
            case "object":
                if (this.props.value instanceof Date)
                    return RexDateNoTime.createFromString((this.props.value as Date).toString()).toDateString();
                else
                    return (this.props.value as RexDateTime).toDateString();
            default:
                throw new Error("Invalid value type for date input type");
            }

        default:
            switch (typeof this.props.value)
            {
            case "number":
                return this.props.value.toString();
            case "string":
                return this.props.value as string;
            case "object":
                if (this.props.value instanceof Date)
                    return (this.props.value as Date).toString();
                else
                    return (this.props.value as RexDateTime).isoString;
            default:
                throw new Error("Invalid value type for date input type");
            }
        }
    }

    /*----------------------------------------------------------------------------
        %%Function: handleChangeBeforeBlur
        %%Qualified: RexInput.handleChangeBeforeBlur

        we are getting change events, but we haven't left the control. remember
        these changes (so we can maintain state), but don't commit the actual
        field yet
    ----------------------------------------------------------------------------*/
    handleChangeBeforeBlur(value: string)
    {
        this.setState({ unblurredValue: value });
    }

    /*----------------------------------------------------------------------------
        %%Function: handleBlur
        %%Qualified: RexInput.handleBlur

        We are leaving the control so its time to finally commit the value (which
        we are given)
    ----------------------------------------------------------------------------*/
    handleBlur(value: string)
    {
        this.props.onChange(this.props.controlName, value);
    }

    /*----------------------------------------------------------------------------
        %%Function: handleKeyDown
        %%Qualified: RexInput.handleKeyDown

        handle the enter key to make sure we blur the control before we leave
    ----------------------------------------------------------------------------*/
    handleKeyDown(event: React.KeyboardEvent<HTMLInputElement>)
    {
        if (event.key === "Enter")
        {
            console.log("got keydown...");
            this.handleBlur(event.currentTarget.value);
        }
    }

    static fieldValueFromInputValue(inputType: InputType, value: string): any
    {
        switch (inputType)
        {
        case InputTypes.DateTime:
            return RexDateTime.createFromString(value);
        case InputTypes.Date:
            return RexDateNoTime.createFromString(value);
        case InputTypes.Integer:
            return parseInt(value);
        default:
            return value;
        }
    }

    render()
    {
        // const value = this.getValueForField();
        const value = this.state.unblurredValue ?? this.getValueForField();

        const inputType = this.getFluentInputType();

        return (
            <div>
                <Field label={this.props.label} required={this.props.required} hint={this.props.hint}>
                    <Input
                        required={this.props.required}
                        readOnly={this.props.readonly}
                        id={this.props.controls.idForControl(this.props.controlName)}
                        name={this.props.controls.nameForControl(this.props.controlName)}
                        onChange={(e) => this.handleChangeBeforeBlur(e.target.value)}
                        onBlur={(e) => this.handleBlur(e.target.value)}
                        onKeyDown={this.handleKeyDown.bind(this)}
                        value={value}
                        type={inputType}
                        style={this.props.style}/>
                </Field>
            </div>
        );
    }
};